import React, { useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons';
import classnames from 'classnames';

import { createStyles } from 'utils/createStyle';
import { CartLineItem } from 'common/api/e-comm/models/CartLineItem';
import { ProductType } from 'common/api/e-comm/models/Product';
import { OrderScreenContext } from 'features/order-summary/screens/CheckoutScreen';
import { useQuantityControls } from 'hooks/useCartLineQuantityControls';

const styles = createStyles({
    plainText: ['text-right sm:text-center', { minWidth: 84 }],
    quantity: [
        'text-center',
        {
            lineHeight: '14px',
            width: 36,
        },
    ],
    quantityContainer: [
        'edit_in_cart_quantity py-1 items-center justify-between flex gp-2',
        { minWidth: 70 },
    ],
    button: [
        'w-8 sm:w-6 h-8 sm:h-6 font-semibold border border-1 border-grey-light rounded-md text-xs cursor-pointer',
        { boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.06)' },
    ],
});

interface QuantityRendererProps {
    item: CartLineItem;
    hideButtons: boolean;
    setShowItemUpdateErrorModal: (_: boolean) => void;
}

export const QuantityRenderer = ({
    item,
    hideButtons,
    setShowItemUpdateErrorModal,
}: QuantityRendererProps) => {
    const Renderer =
        hideButtons || item.giftDetail || item.product.type === ProductType.Reload
            ? PlainTextRenderer
            : EditableQuantityRenderer;
    return (
        <div className="flex items-center justify-start sm:justify-center text-center text-sm text-grey-1">
            <Renderer
                setShowItemUpdateErrorModal={setShowItemUpdateErrorModal}
                hideButtons={hideButtons}
                item={item}
            />
        </div>
    );
};

const PlainTextRenderer = ({ item }: QuantityRendererProps) => (
    <span className={styles.plainText}>{item.quantity}</span>
);

const EditableQuantityRenderer = ({ item, setShowItemUpdateErrorModal }: QuantityRendererProps) => {
    const { setUpdatingLineItem, updatingLineItem } = useContext(OrderScreenContext);
    const { decrement, increment, quantity } = useQuantityControls(
        item,
        setShowItemUpdateErrorModal,
        setUpdatingLineItem
    );
    const buttonStyles = classnames(styles.button, updatingLineItem ? 'bg-grey-accent' : '');
    const minusButtonStyles = classnames(buttonStyles, quantity === 1 ? 'bg-grey-accent' : '');

    return (
        <div className={styles.quantityContainer}>
            <button
                disabled={quantity === 1 || updatingLineItem}
                onClick={quantity === 1 ? undefined : decrement}
                className={minusButtonStyles}
            >
                <FontAwesomeIcon
                    className={quantity === 1 || updatingLineItem ? 'text-grey-2' : 'text-brand'}
                    icon={faMinus}
                />
            </button>
            <span className={styles.quantity}>{quantity}</span>
            <button disabled={updatingLineItem} onClick={increment} className={buttonStyles}>
                <FontAwesomeIcon className="text-brand" icon={faPlus} />
            </button>
        </div>
    );
};
